/**
 * Shopify App Bridge Integration
 *
 * Handles embedded app functionality when running inside Shopify Admin.
 * Provides session token retrieval for authenticated API calls.
 */

import createApp, { ClientApplication } from '@shopify/app-bridge';
import { getSessionToken } from '@shopify/app-bridge/utilities';

// Store the App Bridge instance
let appBridge: ClientApplication | null = null;
let embedContext: {
  host: string;
  shop: string;
  apiKey: string;
} | null = null;

/**
 * Check if the app is running in embedded mode (inside Shopify Admin)
 */
export function isEmbedded(): boolean {
  // Check for host parameter in URL (indicates embedded context)
  const urlParams = new URLSearchParams(window.location.search);
  const host = urlParams.get('host');
  return !!host;
}

/**
 * Get the shop domain from URL parameters
 */
export function getShopFromUrl(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('shop');
}

/**
 * Get the host parameter from URL (required for embedded apps)
 */
export function getHostFromUrl(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('host');
}

/**
 * Initialize App Bridge for embedded app context
 * Should be called early in the app lifecycle when embedded
 *
 * @param apiKey - Shopify Partner App API key (client ID)
 * @returns True if initialization was successful
 */
export function initAppBridge(apiKey: string): boolean {
  const host = getHostFromUrl();
  const shop = getShopFromUrl();

  if (!host) {
    console.log('Not in embedded context - App Bridge not initialized');
    return false;
  }

  try {
    // Create App Bridge instance
    appBridge = createApp({
      apiKey,
      host,
      forceRedirect: true // Redirect to embedded version if not in iframe
    });

    // Store context for later use
    embedContext = {
      host,
      shop: shop || '',
      apiKey
    };

    console.log('App Bridge initialized for shop:', shop);
    return true;
  } catch (error) {
    console.error('Failed to initialize App Bridge:', error);
    return false;
  }
}

/**
 * Get the App Bridge instance
 * Returns null if not in embedded context or not initialized
 */
export function getAppBridge(): ClientApplication | null {
  return appBridge;
}

/**
 * Get embedded context information
 */
export function getEmbedContext() {
  return embedContext;
}

/**
 * Get a Shopify session token for API authentication
 * This token can be used in Authorization headers for backend API calls
 *
 * @returns Session token string or null if not in embedded context
 */
export async function getShopifySessionToken(): Promise<string | null> {
  if (!appBridge) {
    return null;
  }

  try {
    const token = await getSessionToken(appBridge);
    return token;
  } catch (error) {
    console.error('Failed to get Shopify session token:', error);
    return null;
  }
}

/**
 * Create a token getter function for use with the API client
 * This returns either a Shopify session token (if embedded) or falls back to the Clerk getter
 *
 * @param clerkTokenGetter - Fallback function to get Clerk JWT token
 * @returns Token getter function that returns appropriate token based on context
 */
export function createDualAuthTokenGetter(
  clerkTokenGetter: () => Promise<string | null>
): () => Promise<string | null> {
  return async () => {
    // If we're in embedded context and have App Bridge, use Shopify session token
    if (isEmbedded() && appBridge) {
      const shopifyToken = await getShopifySessionToken();
      if (shopifyToken) {
        return shopifyToken;
      }
      // Fall back to Clerk if session token fails
      console.warn('Shopify session token unavailable, falling back to Clerk');
    }

    // Use Clerk JWT for standalone mode
    return clerkTokenGetter();
  };
}

/**
 * Redirect to OAuth installation flow
 * Used when app is accessed without valid session
 *
 * @param shop - Shopify store domain
 * @param apiBaseUrl - Base URL of the API
 */
export function redirectToInstall(shop: string, apiBaseUrl: string): void {
  const installUrl = `${apiBaseUrl}/oauth/install?shop=${encodeURIComponent(shop)}`;
  window.location.href = installUrl;
}

/**
 * Cleanup App Bridge instance
 * Call this when the app unmounts or user logs out
 */
export function destroyAppBridge(): void {
  appBridge = null;
  embedContext = null;
}

/**
 * Check if App Bridge has been initialized
 */
export function isAppBridgeInitialized(): boolean {
  return appBridge !== null;
}
